import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Flex, TabPanel, TabPanels, useToast, Spinner } from "@chakra-ui/react";
import { deleteUserAction, updateUserStatus } from "../../../../actions/userActions";
import { primary, white } from "../../../../utils/colors";
import SidePanel from "../../../../components/sidepanelNew/SidePanel";
import EditEmployee from "../sidepanels/EditEmployee";
import { useTranslation } from "react-i18next";
import useScrollToTop from "../../../../hooks/useScrollToTop";
import useRoleCheck from "../../../../hooks/useRoleCheck";
import ToastComponent from "../../../../components/partials/ToastComponent";
import useThemeColors from "../../../../hooks/useThemeColors";
import EmployeeHeader from "./partials/EmployeeHeader";
import EmployeeDetails from "./partials/EmployeeDetails";
import ConfirmDeleteModal from "../../../../components/modals/ConfirmDeleteModal";
import {
  USER_UPDATE_RESET,
  USER_UPDATE_STATUS_RESET,
} from "../../../../constants/userConstants";
import ConfirmStatusUpdateModal from "../../../../components/modals/ConfirmStatusUpdateModal";
import EmployeeCoordinatorTreatmentPlans from "./partials/EmployeeCoordinatorTreatmentPlans";
import EmployeeWorkSchedule from "./partials/EmployeeWorkSchedule";
import EmployeeEmploymentDetails from "./partials/EmployeeEmploymentDetails";
import { Helmet } from "react-helmet";
import { getEmployeeDetailsAction } from "../../../../actions/employeeActions";
import EmployeeDocuments from "./partials/EmployeeDocuments";
import TabsComponentAlt from "../../../../components/tabs/TabsComponentAlt";

const EmployeeSingle = () => {
  useScrollToTop();

  // GENERAL
  const { slug } = useParams();
  const employeeId = useParams().id;
  const dispatch = useDispatch();
  const toastComponent = ToastComponent();
  const { t } = useTranslation();
  const toast = useToast();

  // COLORS
  const { whiteMode, backgroundLightMode, textPrimaryMode } = useThemeColors();

  // ROLES
  const isDoctor = useRoleCheck("employee_doctor");
  // const isAdmin = useRoleCheck("admin");

  // STATE
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showStatusUpdateConfirmation, setShowStatusUpdateConfirmation] =
    useState(false);
  const [employeeName, setEmployeeName] = useState("");
  const [loadingDisplay, setLoadingDisplay] = useState(true);
  const [editSidePanelOpen, setEditSidePanelOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const tabsData = useMemo(
    () => [
      { label: t("employee.workScheduleTab", "Work schedule") },
      { label: t("employee.documentsTab", "Documents") },
    ],
    [t]
  );

  //  SIDEPANELS
  const [createEmployeeSidePanel, setCreateEmployeeSidePanel] = useState(false);
  const [editEmployeeSidePanel, setEditEmployeeSidePanel] = useState(false);

  // REDUX
  const authLogin = useSelector((state) => state.authLogin);
  const { userInfo } = authLogin;

  const employeeDetails = useSelector((state) => state.employeeDetails);
  const { loading, error, employee } = employeeDetails;

  const userUpdate = useSelector((state) => state.userUpdate);
  const {
    success: successUpdate,
    error: errorUpdate,
    loading: loadingUpdate,
  } = userUpdate;

  const userUpdateStatus = useSelector((state) => state.userUpdateStatus);
  const {
    success: successUpdateStatus,
    error: errorUpdateStatus,
    loading: loadingUpdateStatus,
  } = userUpdateStatus;

  // HANDLERS
  const deleteHandler = () => {
    dispatch(deleteUserAction(employeeId));
  };

  const openEditEmployeeSidePanel = () => {
    setEditEmployeeSidePanel(true);
    document.body.style.overflow = "hidden";
  };

  const closeEditEmployeeSidePanel = () => {
    setEditEmployeeSidePanel(false);
    document.body.style.overflow = "auto";
  };

  // FETCH DATA
  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoadingDisplay(false);
    }, 500); // CHANGE THIS TO HAVE A LONGER LOADING TIME

    // Cleanup function to clear the timeout if the component unmounts
    return () => clearTimeout(timeout);
  }, []);

  const fetchDetails = useCallback(() => {
    if (slug && employeeId) {
      dispatch(getEmployeeDetailsAction(slug, employeeId));
    }
  }, [dispatch, slug, employeeId]);

  // USE EFFECT

  useEffect(() => {
    if (
      !employee ||
      employee._id !== employeeId ||
      successUpdate ||
      successUpdateStatus
    ) {
      fetchDetails();
    }
  }, [employee, employeeId, successUpdate, successUpdateStatus, fetchDetails]);

  useEffect(() => {
    if (successUpdate) {
      toastComponent.showToast(t("toasts.employeeUpdate"), "success", 3000);
      dispatch({ type: USER_UPDATE_RESET });

      closeEditEmployeeSidePanel();
    } else if (errorUpdate) {
      toastComponent.showToast(t("toasts.error"), "error", 3000);
    }
  }, [dispatch, successUpdate, errorUpdate]);

  useEffect(() => {
    if (successUpdateStatus) {
      toastComponent.showToast(
        t("toasts.employeeUpdateStatus"),
        "success",
        3000
      );

      dispatch({ type: USER_UPDATE_STATUS_RESET });
    } else if (errorUpdateStatus) {
      toastComponent.showToast(
        t("toasts.employeeUpdateStatusError"),
        "error",
        3000
      );
    }
  }, [dispatch, successUpdateStatus, errorUpdateStatus]);

  // LOGS
  // console.log("employeeId", employeeId);

  return (
    <>
      {/* HELMET  */}
      <Helmet>
        <title>
          {employee && employee.userID
            ? `${t("pageTitle.employeeSingle")} - ${employee.userID}`
            : t("pageTitle.employeeSingle")}
        </title>
        <meta
          name="description"
          content={t("pageDescription.employeeSingle")}
        />
      </Helmet>

      {/* SIDEPANELS  START */}
      <SidePanel
        open={editEmployeeSidePanel}
        close={closeEditEmployeeSidePanel}
        title={t("employee.editEmployee")}
        noBorder={true}
      >
        <Flex
          w="100%"
          bg={white}
          minW="800px"
          maxW="800px"
          position="relative"
          zIndex="900"
        >
          <>
            <EditEmployee
              employee={employee}
              employeeId={employeeId}
              close={closeEditEmployeeSidePanel}
            />
          </>
        </Flex>
      </SidePanel>
      {/* SIDEPANELS  END */}

      {/* MODALS START */}
      {showDeleteConfirmation && (
        <ConfirmDeleteModal
          deleteTitle={t("employee.deleteEmployeeTitle")}
          deleteName={employeeName}
          warningLabel={t("employee.deleteWarning")}
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          confirmAction={() => {
            deleteHandler(employeeId);
            setShowDeleteConfirmation(false);
          }}
        />
      )}

      {showStatusUpdateConfirmation && (
        <ConfirmStatusUpdateModal
          headerTitle={t("employee.updateEmployeeStatusHeaderTitle")}
          title={t("employee.updateEmployeeStatusTitle")}
          name={
            employee && employee.status !== "active"
              ? t("employeeStatus.active")
              : t("employeeStatus.inactive")
          }
          warningLabel={
            employee && employee.status !== "active"
              ? t("employee.updateEmployeeStatusWarningActive")
              : t("employee.updateEmployeeStatusWarningInactive")
          }
          isOpen={showStatusUpdateConfirmation}
          onClose={() => setShowStatusUpdateConfirmation(false)}
          confirmAction={() => {
            dispatch(
              updateUserStatus(employeeId, {
                status:
                  employee && employee.status !== "active"
                    ? "active"
                    : "inactive",
              })
            );
            setShowStatusUpdateConfirmation(false);
          }}
        />
      )}
      {/* MODALS END */}

      {loadingDisplay || loading ? (
        <Flex direction="column" w="100%" h="100%" p="calc(80px + 2rem) 2rem">
          <Flex
            w="100%"
            align="center"
            justify="center"
            bg={whiteMode}
            borderRadius="2rem"
            minH="90vh"
            p="3rem 0"
          >
            <Spinner size="xl" color={primary} />
          </Flex>
        </Flex>
      ) : (
        <>
          {employee && (
            <Flex
              direction="column"
              w="100%"
              // h="100%"
              p="calc(80px + 2rem) 2rem 3rem 2rem"
            >
              <Flex
                w="100%"
                direction="column"
                bg={whiteMode}
                borderRadius="2rem"
              >
                <EmployeeHeader
                  slug={slug}
                  employee={employee}
                  isDoctor={isDoctor}
                  openEdit={openEditEmployeeSidePanel}
                  setShowDeleteConfirmation={setShowDeleteConfirmation}
                  setEmployeeName={setEmployeeName}
                  setShowStatusUpdateConfirmation={
                    setShowStatusUpdateConfirmation
                  }
                />

                <Flex
                  w="100%"
                  justify="space-between"
                  p={[
                    "105px 1.5rem 3rem 1.5rem",
                    "105px 2rem 3rem 2rem",
                    "0px 2rem 2rem 2rem",
                  ]}
                  direction={["column", "row", "row"]}
                >
                  <Flex
                    w={["100%", "calc(100% - 350px)", "calc(100% - 350px)"]}
                    direction="column"
                    p={[
                      "2rem 0rem 0 0rem",
                      "2rem 2rem 0 0rem",
                      "2rem 2rem 0 0rem",
                    ]}
                  >
                    <EmployeeDetails
                      user={employee}
                      openEdit={openEditEmployeeSidePanel}
                    />

                    <Box mt="2rem">
                      <TabsComponentAlt
                        activeTab={activeTab}
                        tabs={tabsData}
                        setActiveTab={setActiveTab}
                        isTitleValid={true}
                        disabledTabs={[]}
                        isSinglePage
                      >
                        <TabPanels>
                          <TabPanel px={0} pb={0}>
                            <EmployeeWorkSchedule
                              slug={slug}
                              employee={employee}
                              employeeId={employeeId}
                              onScheduleUpdated={fetchDetails}
                            />
                          </TabPanel>
                          <TabPanel px={0} pb={0}>
                            <EmployeeDocuments
                              slug={slug}
                              employeeId={employeeId}
                              employee={employee}
                              onDocumentsUpdated={fetchDetails}
                            />
                          </TabPanel>
                        </TabPanels>
                      </TabsComponentAlt>
                    </Box>

                    {/* <EmployeeCoordinatorTreatmentPlans
                      // patient={patient}
                      employeeId={employeeId}
                      // buttonHandler={openCreatePatientTreatmentPlanSidePanel}
                      userInfo={userInfo}
                      // isModalSending={isModalSending}
                      // setIsModalSending={setIsModalSending}
                      // fetchUserDetails={fetchUserDetails}
                      // fetchUnavailable={fetchUnavailable}
                      // setFetchUnavailable={setFetchUnavailable}
                    /> */}
                  </Flex>

                  <Flex w="350px" direction="column" p="2rem 0rem 0 0rem">
                    <EmployeeEmploymentDetails
                      employee={employee}
                      openEdit={openEditEmployeeSidePanel}
                    />
                    {/* <PatientActivityLog
                      id={patientId}
                      currentLanguage={currentLanguage}
                    /> */}
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          )}
        </>
      )}
    </>
  );
};

export default EmployeeSingle;
